import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCustomerById,
  fetchCustomerLedger,
  archiveCustomerRpc,
  restoreCustomerRpc,
  deleteCustomerRpc,
} from '@/services/customers';
import { fetchShopSettings } from '@/services/settings';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Customer } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { CustomerForm } from './CustomerForm';
import { printCustomerStatement } from '@/lib/customerStatementPrinter';
import { useAuth } from '@/context/AuthContext';
import {
  Edit2,
  Loader2,
  Printer,
  Archive,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

interface CustomerDetailProps {
  customerId: string;
  onClose: () => void;
  onOpenSale?: (saleId: string) => void;
  onOpenRepair?: (repairId: string) => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const CustomerDetail: React.FC<CustomerDetailProps> = ({
  customerId,
  onClose,
}) => {
  const { role } = useAuth();
  const isOwner = role === 'owner';
  const isManagerOrOwner = role === 'owner' || role === 'manager';
  const queryClient = useQueryClient();

  const [showEditForm, setShowEditForm] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: customer, isLoading, error, refetch } = useQuery<Customer>({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: () => fetchCustomerById(customerId),
  });

  const { data: ledger, isLoading: loadingLedger } = useQuery({
    queryKey: ['customers', 'ledger', customerId],
    queryFn: () => fetchCustomerLedger(customerId),
    enabled: !!customer,
  });

  const { data: shopSettings } = useQuery({
    queryKey: ['shop-settings'],
    queryFn: () => fetchShopSettings(),
  });

  // Archive Mutation
  const archiveMutation = useMutation({
    mutationFn: () => {
      if (!archiveReason.trim()) throw new Error('An archive reason is required.');
      return archiveCustomerRpc(customerId, archiveReason.trim());
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
      setShowArchiveDialog(false);
      setArchiveReason('');
    },
    onError: (err) => setActionError(parseDatabaseError(err)),
  });

  // Restore Mutation
  const restoreMutation = useMutation({
    mutationFn: () => restoreCustomerRpc(customerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
    },
    onError: (err) => setActionError(parseDatabaseError(err)),
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomerRpc(customerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      setShowDeleteDialog(false);
      onClose();
    },
    onError: (err) => setActionError(parseDatabaseError(err)),
  });

  const handlePrintStatement = () => {
    if (!customer || !ledger || !shopSettings) return;
    printCustomerStatement({
      shop: shopSettings,
      customer,
      ledger,
    });
  };

  if (showEditForm && customer) {
    return (
      <Modal isOpen title={`Edit — ${customer.name}`} onClose={() => setShowEditForm(false)}>
        <CustomerForm
          customer={customer}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
            setShowEditForm(false);
          }}
          onCancel={() => setShowEditForm(false)}
        />
      </Modal>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && (
        <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />
      )}

      {customer && !isLoading && (
        <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
          {actionError && <ErrorAlert message={actionError} />}

          {/* Archived Banner */}
          {customer.archived_at && (
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              <div className="flex items-center space-x-2">
                <Archive className="w-4 h-4 text-amber-600" />
                <span>
                  <strong>Archived Customer:</strong> {customer.archive_reason || 'Archived record'}{' '}
                  ({formatDate(customer.archived_at)})
                </span>
              </div>
              {isManagerOrOwner && (
                <button
                  type="button"
                  onClick={() => restoreMutation.mutate()}
                  disabled={restoreMutation.isPending}
                  className="px-2.5 py-1 bg-amber-700 text-white font-semibold rounded hover:bg-amber-800 transition-colors text-[11px]"
                >
                  {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
                </button>
              )}
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-brand-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-brand-green text-white flex items-center justify-center text-lg font-bold uppercase">
                {customer.name.charAt(0)}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-bold text-brand-black">{customer.name}</h3>
                  {customer.customer_code && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] font-mono font-bold rounded">
                      {customer.customer_code}
                    </span>
                  )}
                </div>
                <p className="text-xs text-brand-muted">
                  {customer.phone || 'No phone'} • {customer.email || 'No email'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handlePrintStatement}
                disabled={!ledger || loadingLedger}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-brand-border text-brand-black text-xs font-semibold rounded-md hover:bg-brand-bg transition-colors shadow-sm"
              >
                <Printer className="w-3.5 h-3.5 text-brand-green" />
                <span>Statement</span>
              </button>

              <button
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
              >
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>

              {isManagerOrOwner && !customer.archived_at && (
                <button
                  type="button"
                  onClick={() => setShowArchiveDialog(true)}
                  className="inline-flex items-center space-x-1 px-2.5 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-md hover:bg-amber-100 transition-colors"
                >
                  <Archive className="w-3.5 h-3.5" />
                  <span>Archive</span>
                </button>
              )}

              {isOwner && (
                <button
                  type="button"
                  onClick={() => setShowDeleteDialog(true)}
                  className="inline-flex items-center space-x-1 px-2.5 py-1.5 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-md hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>

          {/* Account Summary Cards */}
          {ledger && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="p-2.5 bg-brand-bg rounded-lg border border-brand-border">
                <span className="text-[10px] uppercase font-bold text-brand-muted block">Total Invoiced</span>
                <span className="text-sm font-extrabold text-brand-black">{formatMoney(ledger.totalSales)}</span>
                <span className="text-[10px] text-brand-muted block mt-0.5">{ledger.salesCount} sale(s)</span>
              </div>

              <div className="p-2.5 bg-brand-bg rounded-lg border border-brand-border">
                <span className="text-[10px] uppercase font-bold text-brand-muted block">Paid Receipts</span>
                <span className="text-sm font-extrabold text-emerald-700">{formatMoney(ledger.totalPaymentsReceived)}</span>
                <span className="text-[10px] text-brand-muted block mt-0.5">Gross collections</span>
              </div>

              <div className="p-2.5 bg-brand-bg rounded-lg border border-brand-border">
                <span className="text-[10px] uppercase font-bold text-brand-muted block">Return Credits</span>
                <span className="text-sm font-extrabold text-amber-700">{formatMoney(ledger.totalReturnCredit)}</span>
                <span className="text-[10px] text-brand-muted block mt-0.5">Cash Ref: {formatMoney(ledger.totalCashRefunded)}</span>
              </div>

              <div className={`p-2.5 rounded-lg border ${
                ledger.outstandingBalance > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
              }`}>
                <span className={`text-[10px] uppercase font-bold block ${
                  ledger.outstandingBalance > 0 ? 'text-red-800' : 'text-green-800'
                }`}>
                  Outstanding Balance
                </span>
                <span className={`text-sm font-black ${
                  ledger.outstandingBalance > 0 ? 'text-red-700' : 'text-green-700'
                }`}>
                  {formatMoney(ledger.outstandingBalance)}
                </span>
                <span className="text-[10px] text-brand-muted block mt-0.5">Authoritative balance</span>
              </div>
            </div>
          )}

          {/* Account Ledger Timeline */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-brand-black uppercase tracking-wider">
                Account Ledger & Activity Timeline
              </h4>
              <span className="text-[10px] text-brand-muted">
                {ledger?.entries.length || 0} transaction(s)
              </span>
            </div>

            <div className="bg-white border border-brand-border rounded-lg overflow-hidden text-xs">
              <table className="w-full text-left">
                <thead className="bg-brand-bg border-b border-brand-border text-[10px] uppercase text-brand-muted font-bold">
                  <tr>
                    <th className="p-2.5">Date</th>
                    <th className="p-2.5">Ref #</th>
                    <th className="p-2.5">Description</th>
                    <th className="p-2.5 text-right">Debit (+)</th>
                    <th className="p-2.5 text-right">Credit (-)</th>
                    <th className="p-2.5 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {loadingLedger ? (
                    <tr>
                      <td colSpan={6} className="text-center py-6 text-brand-muted">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading ledger timeline…
                      </td>
                    </tr>
                  ) : !ledger || ledger.entries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-6 text-brand-muted">
                        No transactions recorded for this customer yet.
                      </td>
                    </tr>
                  ) : (
                    ledger.entries.map((e) => (
                      <tr key={e.id} className="hover:bg-brand-bg/50 transition-colors">
                        <td className="p-2.5 font-mono text-[11px] text-brand-muted">{formatDate(e.date)}</td>
                        <td className="p-2.5 font-mono font-bold text-brand-black">{e.reference}</td>
                        <td className="p-2.5 text-brand-black">{e.description}</td>
                        <td className="p-2.5 text-right font-medium text-brand-black">
                          {e.debit > 0 ? formatMoney(e.debit) : '—'}
                        </td>
                        <td className="p-2.5 text-right font-medium text-emerald-700">
                          {e.credit > 0 ? formatMoney(e.credit) : '—'}
                        </td>
                        <td className="p-2.5 text-right font-bold text-brand-black">
                          {formatMoney(e.runningBalance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveDialog && (
        <Modal
          isOpen={showArchiveDialog}
          title="Archive Customer"
          onClose={() => setShowArchiveDialog(false)}
        >
          <div className="space-y-4 text-xs">
            <p className="text-brand-black">
              Archiving excludes this customer from active sales and Quick Add duplicate searches,
              while safely preserving all historical invoices, warranties, and repairs.
            </p>
            <div>
              <label className="block font-bold text-brand-black mb-1">
                Reason for Archiving <span className="text-brand-red">*</span>
              </label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                rows={2}
                placeholder="e.g. Customer requested account closure, inactive contact..."
                className="w-full border border-brand-border rounded p-2 text-xs focus:ring-2 focus:ring-brand-green"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2 border-t border-brand-border">
              <button
                type="button"
                onClick={() => setShowArchiveDialog(false)}
                className="px-3 py-1.5 text-brand-muted hover:text-brand-black"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending || !archiveReason.trim()}
                className="px-4 py-1.5 bg-amber-600 text-white font-bold rounded hover:bg-amber-700 disabled:opacity-50"
              >
                {archiveMutation.isPending ? 'Archiving…' : 'Archive Customer'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Permanent Delete Modal (Owner Only) */}
      {showDeleteDialog && (
        <Modal
          isOpen={showDeleteDialog}
          title="Permanent Delete Customer"
          onClose={() => setShowDeleteDialog(false)}
        >
          <div className="space-y-4 text-xs">
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-900 flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
              <div>
                <p className="font-bold">Delete this unused customer permanently?</p>
                <p className="mt-1">
                  Permanent deletion is allowed ONLY if the customer has no sales, returns, repairs, or warranty history.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t border-brand-border">
              <button
                type="button"
                onClick={() => setShowDeleteDialog(false)}
                className="px-3 py-1.5 text-brand-muted hover:text-brand-black"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-1.5 bg-red-600 text-white font-bold rounded hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Close Footer */}
      <div className="flex justify-end pt-4 mt-4 border-t border-brand-border">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors"
        >
          Close
        </button>
      </div>
    </>
  );
};

